import Image from "next/image"
import type { ReferencePhoto } from "@/data/grass-reference-photos"
import type { ProductRecommendationLink } from "@/lib/product-picks/types"
import { VersusBuyChip } from "@/components/compare/versus-buy-chip"

/**
 * The "VS" versus panel — the signature element of the liquid-glass compare
 * refresh. A green (A) / amber (B) split with a center VS badge.
 *
 * For grass comparisons each side carries the verified, attributed reference
 * photo (from data/grass-reference-photos.ts via getGrassReferencePhotoMeta) so
 * the panel doubles as the visual ID the prototype lacked — recognition over
 * recall (obsidian/research/growth-design/amber-alert.md) and image-intent SEO.
 * Brand comparisons (Scotts vs Pennington) pass no photo and render text-only.
 *
 * Server-rendered (no client hooks) so the <img> + alt land in crawlable HTML.
 */
export interface VersusSide {
  /** Display name, e.g. "Kentucky Bluegrass". */
  name: string
  /** Season label (grass) or product type (brand) shown under the name. */
  sub?: string | null
  /** Verified reference photo (grass and topic sides); omit/null for brand sides. */
  photo?: ReferencePhoto | null
  /**
   * Overrides the default "close-up of the grass blades" alt text. Topic sides
   * are not always grass (a weed, a clover patch, a turf yard), so they name
   * what the photo actually shows.
   */
  photoAlt?: string
  /** Product shot (brand sides); rendered letterboxed on white, not cropped. */
  productImage?: string | null
  /**
   * Fully composed artwork for sides that have neither a photo nor a packshot
   * but still deserve a designed tile (app matchups). Takes precedence over
   * every other media slot. Decorative: the side name renders below it.
   */
  tile?: React.ReactNode
  /**
   * Fallback tile icon for sides with neither a photo nor a product image
   * (e.g. Sunday, a mail-order subscription with no Amazon SKU) so the split
   * stays visually balanced.
   */
  fallbackIcon?: React.ReactNode
  /** Optional affiliate product for this side; renders a "View on Amazon" chip. */
  buy?: ProductRecommendationLink | null
}

function Side({ side, variant, slug }: { side: VersusSide; variant: "a" | "b"; slug: string }) {
  return (
    <div className={`cmp-versus__side cmp-versus__side--${variant}`}>
      {side.tile ? (
        <div className="cmp-versus__media cmp-versus__media--tile">{side.tile}</div>
      ) : side.photo ? (
        <div className="cmp-versus__media">
          <Image
            src={side.photo.src}
            alt={side.photoAlt ?? `What ${side.name} looks like, a close-up of the grass blades`}
            fill
            sizes="(max-width: 640px) 45vw, 300px"
          />
        </div>
      ) : side.productImage ? (
        <div className="cmp-versus__media cmp-versus__media--product">
          <Image
            src={side.productImage}
            // The pictured product can differ from the side label (e.g. the
            // "DIY" side shows its recommended fertilizer), so name the actual
            // product when we know it.
            alt={`${side.buy?.name ?? side.name} product packaging`}
            fill
            sizes="(max-width: 640px) 45vw, 300px"
          />
        </div>
      ) : side.fallbackIcon ? (
        <div className="cmp-versus__media cmp-versus__media--icon" aria-hidden="true">
          {side.fallbackIcon}
        </div>
      ) : null}
      <span className="cmp-versus__name">{side.name}</span>
      {side.sub && <span className="cmp-versus__sub">{side.sub}</span>}
      {side.buy && <VersusBuyChip product={side.buy} slug={slug} variant={variant} />}
    </div>
  )
}

/**
 * Human-readable source host for a reference-photo sourceUrl. Photos come from
 * Wikimedia Commons OR iNaturalist (research-grade observations), so the credit
 * line can't hardcode one — see data/grass-reference-photos.ts.
 */
function photoSourceName(sourceUrl: string): string {
  if (sourceUrl.includes("inaturalist.org")) return "iNaturalist"
  if (sourceUrl.includes("wikimedia.org") || sourceUrl.includes("wikipedia.org"))
    return "Wikimedia Commons"
  return "its source"
}

/**
 * Photo attribution block, rendered at the very bottom of the page (rather than
 * inside the hero panel) — CC BY/BY-SA only require attribution somewhere
 * reasonable on the page, and the hero stays clean. Dedupes by source URL and
 * skips attribution-free (CC0 / public-domain) photos.
 */
export function ComparePhotoCredits({
  photos,
}: {
  photos: Array<{ name: string; photo: ReferencePhoto | null | undefined }>
}) {
  const seen = new Set<string>()
  const credited = photos.filter(({ photo }) => {
    if (!photo?.credit) return false
    if (seen.has(photo.sourceUrl)) return false
    seen.add(photo.sourceUrl)
    return true
  })
  if (credited.length === 0) return null
  return (
    <p className="cmp-credits">
      {credited.map(({ name, photo }, i) => (
        <span key={photo!.sourceUrl}>
          {i > 0 && " · "}
          {name} photo: {photo!.credit},{" "}
          <a href={photo!.sourceUrl} target="_blank" rel="noopener noreferrer nofollow">
            {photo!.license}
          </a>
          , via {photoSourceName(photo!.sourceUrl)}
        </span>
      ))}
    </p>
  )
}

export function CompareVersusPanel({ a, b, slug }: { a: VersusSide; b: VersusSide; slug: string }) {
  return (
    <div className="cmp-versus">
      <Side side={a} variant="a" slug={slug} />
      <span className="cmp-versus__vs" aria-hidden="true">
        VS
      </span>
      <Side side={b} variant="b" slug={slug} />
    </div>
  )
}
